import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';

import type { TrackingProvider } from '../interfaces/tracking-provider.interface';
import type { TrackingEvent } from '../interfaces/tracking-event.interface';
import { TRACKING_CONFIG } from '../tracking.tokens';

interface FbqTestOptions {
  eventID?: string;
  test_event_code?: string;
}
type FbqFn = (
  command: string,
  eventName: string,
  ...data: [params?: Record<string, unknown>, opts?: FbqTestOptions]
) => void;

declare global {
  interface Window {
    fbq?: FbqFn & { callMethod?: FbqFn; queue?: unknown[]; loaded?: boolean; version?: string; push?: FbqFn };
    _fbq?: Window['fbq'];
  }
}

const META_STANDARD_EVENTS: Record<string, string> = {
  page_view: 'PageView',
  conversion: 'CompleteRegistration',
  form_start: 'InitiateCheckout',
  form_submit: 'SubmitApplication',
};

@Injectable()
export class MetaPixelTrackingProvider implements TrackingProvider {
  public readonly name = 'meta-pixel';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly config = inject(TRACKING_CONFIG);
  private readonly document = inject(DOCUMENT);
  private readonly esBrowser = isPlatformBrowser(this.platformId);

  private initialized = false;

  public initialize(): void {
    if (!this.esBrowser) return;
    if (!this.config.metaPixel.habilitado) {
      console.log('[MetaPixelProvider] deshabilitado en este ambiente');
      return;
    }
    if (this.initialized) return;
    this.initialized = true;

    const win = this.document.defaultView as Window;

    if (!win.fbq) {
      const fbqFn: NonNullable<Window['fbq']> = function (...args: Parameters<FbqFn>) {
        if (fbqFn.callMethod) {
          fbqFn.callMethod(...args);
        } else {
          fbqFn.queue ??= [];
          fbqFn.queue.push(args);
        }
      };

      fbqFn.push = fbqFn;
      fbqFn.loaded = true;
      fbqFn.version = '2.0';
      fbqFn.queue = [];

      win.fbq = fbqFn;
      win._fbq ??= fbqFn;
    }

    const script = this.document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const [firstScript] = Array.from(this.document.getElementsByTagName('script'));
    firstScript?.parentNode?.insertBefore(script, firstScript);

    const testOptions: FbqTestOptions | undefined = this.config.metaPixel.testEventCode
      ? { test_event_code: this.config.metaPixel.testEventCode }
      : undefined;

    win.fbq?.('init', this.config.metaPixel.pixelId);
    if (testOptions) {
      win.fbq?.('track', 'PageView', {}, testOptions);
    } else {
      win.fbq?.('track', 'PageView');
    }
  }

  public isAvailable(): boolean {
    return this.esBrowser && this.config.metaPixel.habilitado && typeof this.document.defaultView?.fbq === 'function';
  }

  public track(event: TrackingEvent): void {
    if (!this.isAvailable()) return;

    const win = this.document.defaultView as Window;

    const properties: Record<string, unknown> = {
      content_name: event.name,
      content_category: event.category,
      ...(event.flow && { flow: event.flow }),
      ...(event.section && { section: event.section }),
      ...(event.label && { label: event.label }),
      ...(event.state && { state: event.state }),
      ...(event.value !== undefined && { value: event.value }),
      ...event.metadata,
    };

    const standardEvent = META_STANDARD_EVENTS[event.action];
    const testOptions: FbqTestOptions | undefined = this.config.metaPixel.testEventCode
      ? { test_event_code: this.config.metaPixel.testEventCode }
      : undefined;

    if (standardEvent) {
      if (testOptions) {
        win.fbq?.('track', standardEvent, properties, testOptions);
      } else {
        win.fbq?.('track', standardEvent, properties);
      }
    } else if (testOptions) {
      win.fbq?.('trackCustom', event.name, properties, testOptions);
    } else {
      win.fbq?.('trackCustom', event.name, properties);
    }
  }

  public identificarSesion(sessionId: string): void {
    if (!this.isAvailable()) return;
    const win = this.document.defaultView as Window;
    win.fbq?.('init', this.config.metaPixel.pixelId, { external_id: sessionId });
  }
}
